<template>
    <b-modal
        ref="modal"
        :title="modalTitle"
        size="lg"
        centered
        hide-footer
        modal-class="compliance-settlement-modal-shell"
        header-class="compliance-settlement-modal-header"
        body-class="compliance-settlement-modal-body"
        @hidden="onHidden"
    >
        <div v-if="loading" class="compliance-loading">
            <div class="loading-dots"><span></span><span></span><span></span></div>
            <span class="loading-text">이행 정산 정보를 불러오는 중…</span>
        </div>
        <div v-else-if="loadError" class="compliance-error">
            <div class="error-icon-wrap"><i class="fas fa-exclamation-circle"></i></div>
            <span>{{ loadError }}</span>
        </div>
        <div v-else-if="context" class="compliance-settlement-content">
            <!-- 워크플로우 컨텍스트 -->
            <div class="compliance-context-box">
                <i class="fas fa-leaf compliance-context-icon" aria-hidden="true"></i>
                <div class="compliance-context-text">
                    <span class="compliance-context-label">정산 대상</span>
                    <span class="compliance-context-value">{{ workflowName || "워크플로우" }}</span>
                </div>
                <span v-if="isSettled" class="compliance-settled-badge">정산 완료</span>
            </div>

            <!-- 단계 탭 -->
            <div class="compliance-step-tabs" role="tablist">
                <button
                    type="button"
                    class="compliance-step-tab"
                    :class="{ 'is-active': step === 1 }"
                    @click="step = 1"
                >
                    <span class="step-num">1</span>
                    이행 현황
                </button>
                <button
                    type="button"
                    class="compliance-step-tab"
                    :class="{ 'is-active': step === 2, 'is-disabled': step < 2 }"
                    :disabled="step < 2"
                    @click="step >= 2 && (step = 2)"
                >
                    <span class="step-num">2</span>
                    유연성 입력
                </button>
            </div>

            <!-- Step 1: 현황 -->
            <div v-show="step === 1" class="compliance-step-panel">
                <div class="form-section">
                    <div class="section-header">
                        <span class="section-pill" aria-hidden="true"></span>
                        <h6>이행 현황</h6>
                    </div>
                    <dl class="summary-kv-grid">
                        <div class="summary-kv-item">
                            <dt>배출허용량</dt>
                            <dd>{{ formatT(context.assignedCapT) }} <span class="unit">tCO₂e</span></dd>
                        </div>
                        <div class="summary-kv-item">
                            <dt>실측 배출량</dt>
                            <dd>{{ formatT(context.grossActualT) }} <span class="unit">tCO₂e</span></dd>
                        </div>
                        <div class="summary-kv-item">
                            <dt>전년 이월 잔액</dt>
                            <dd>{{ formatT(context.availableCarryInT) }} <span class="unit">tCO₂e</span></dd>
                        </div>
                        <div v-if="context.borrowReserveKg > 0" class="summary-kv-item">
                            <dt>차입 예약(익년)</dt>
                            <dd>{{ formatT(context.borrowReserveKg / 1000) }} <span class="unit">tCO₂e</span></dd>
                        </div>
                    </dl>
                    <p v-if="previewDraft" class="summary-hint">
                        <i class="fas fa-info-circle"></i>
                        부족분(초안) {{ formatT(previewDraft.remainingDeficitKg / 1000) }} tCO₂e · 차입 한도
                        {{ context.borrowCapPct }}% · 상쇄 한도 {{ context.offsetCapPct }}%
                    </p>
                </div>
            </div>

            <!-- Step 2: 유연성 입력 -->
            <div v-show="step === 2" class="compliance-step-panel">
                <div class="form-section form-section-flex">
                    <div class="section-header">
                        <span class="section-pill" aria-hidden="true"></span>
                        <h6>이월 · 차입 · 상쇄</h6>
                    </div>
                    <div class="flex-form-grid">
                        <b-form-group label="이월 사용 (tCO₂e)" label-class="compliance-form-label">
                            <b-form-input
                                v-model.number="form.carryInT"
                                type="number"
                                min="0"
                                :max="maxCarryInT"
                                step="0.001"
                                class="compliance-input"
                                :disabled="isSettled"
                            />
                            <small class="field-hint">최대 {{ formatT(maxCarryInT) }} t (잔액·부족분 이내)</small>
                        </b-form-group>
                        <b-form-group
                            :label="`차입 (tCO₂e, 최대 ${context.borrowCapPct}%)`"
                            label-class="compliance-form-label"
                        >
                            <b-form-input
                                v-model.number="form.borrowT"
                                type="number"
                                min="0"
                                :max="maxBorrowT"
                                step="0.001"
                                class="compliance-input"
                                :disabled="isSettled"
                            />
                            <small class="field-hint">최대 {{ formatT(maxBorrowT) }} t</small>
                        </b-form-group>
                        <b-form-group
                            :label="`상쇄 (tCO₂e, 최대 ${context.offsetCapPct}%)`"
                            label-class="compliance-form-label"
                        >
                            <b-form-input
                                v-model.number="form.offsetT"
                                type="number"
                                min="0"
                                :max="maxOffsetT"
                                step="0.001"
                                class="compliance-input"
                                :disabled="isSettled"
                            />
                            <small class="field-hint">최대 {{ formatT(maxOffsetT) }} t</small>
                        </b-form-group>
                        <b-form-group label="KOC 인증번호" label-class="compliance-form-label">
                            <b-form-input
                                v-model="form.certificateNo"
                                maxlength="100"
                                class="compliance-input"
                                :disabled="isSettled"
                            />
                        </b-form-group>
                        <b-form-group label="상쇄 공급자" label-class="compliance-form-label">
                            <b-form-input
                                v-model="form.vendorName"
                                maxlength="200"
                                class="compliance-input"
                                :disabled="isSettled"
                            />
                        </b-form-group>
                    </div>
                </div>

                <!-- COMPLIANCE 증빙 -->
                <div class="form-section form-section-evidence">
                    <div class="section-header section-header-split">
                        <div class="section-header-left">
                            <span class="section-pill section-pill-muted" aria-hidden="true"></span>
                            <h6>이행 정산 증빙</h6>
                        </div>
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            class="evidence-upload-btn"
                            :disabled="isSettled"
                            @click="openEvidenceModal"
                        >
                            <i class="fas fa-paperclip mr-1"></i>
                            증빙 업로드/삭제
                        </b-button>
                    </div>
                    <p class="evidence-intro">
                        차입/상쇄 금액이 0보다 크면 해당 증빙을 선택해야 합니다. (필요 시 강제확정 가능)
                    </p>

                    <b-form-group
                        v-if="Number(form.borrowT) > 0"
                        label="차입 증빙 (BORROW_APPROVAL)"
                        label-class="compliance-form-label"
                    >
                        <b-form-select
                            v-model.number="form.borrowEvidenceFileIdx"
                            :options="borrowEvidenceOptions"
                            value-field="value"
                            text-field="text"
                            size="sm"
                            class="compliance-input"
                            :disabled="isSettled"
                        />
                    </b-form-group>
                    <b-form-group
                        v-if="Number(form.borrowT) > 0"
                        label="차입 승인/계약 참조값 (선택)"
                        label-class="compliance-form-label"
                    >
                        <b-form-input
                            v-model="form.borrowApprovalRef"
                            maxlength="200"
                            class="compliance-input"
                            :disabled="isSettled"
                        />
                    </b-form-group>

                    <b-form-group
                        v-if="Number(form.offsetT) > 0"
                        label="상쇄 증빙 (OFFSET_CERTIFICATE)"
                        label-class="compliance-form-label"
                    >
                        <b-form-select
                            v-model.number="form.offsetEvidenceFileIdx"
                            :options="offsetEvidenceOptions"
                            value-field="value"
                            text-field="text"
                            size="sm"
                            class="compliance-input"
                            :disabled="isSettled"
                        />
                    </b-form-group>
                </div>

                <div v-if="previewDraft" class="preview-result-box">
                    <div class="preview-result-title">
                        <i class="fas fa-chart-line"></i>
                        정산 미리보기
                    </div>
                    <div class="preview-result-body">
                        정산 후 순배출
                        <strong>{{ formatT(previewDraft.netPositionKg / 1000) }}</strong> tCO₂e · 이행
                        <strong>{{ previewDraft.complianceStatus }}</strong>
                        <span v-if="previewDraft.carryOutGeneratedKg > 0" class="preview-extra">
                            · 익년 이월 후보 {{ formatT(previewDraft.carryOutGeneratedKg / 1000) }} t
                        </span>
                    </div>
                </div>
            </div>

            <div class="compliance-modal-footer">
                <div class="footer-left">
                    <b-button v-if="step > 1" variant="secondary" size="sm" class="footer-btn" @click="step = 1">
                        ← 이전
                    </b-button>
                    <b-button
                        v-if="isSettled"
                        variant="outline-warning"
                        size="sm"
                        class="footer-btn"
                        :disabled="saving"
                        @click="rollbackSettlement"
                    >
                        <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                        이행 정산 롤백
                    </b-button>
                </div>
                <div class="footer-right">
                    <b-button variant="secondary" size="sm" class="footer-btn" @click="hide">취소</b-button>
                    <b-button v-if="step === 1" variant="success" size="sm" class="footer-btn footer-btn-primary" @click="goStep2">
                        다음 단계
                    </b-button>
                    <b-button
                        v-else
                        variant="success"
                        size="sm"
                        class="footer-btn footer-btn-primary"
                        :disabled="saving || isSettled"
                        @click="submit"
                    >
                        <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                        {{ saving ? "저장 중..." : isSettled ? "정산 완료" : "이행 정산 확정" }}
                    </b-button>
                </div>
            </div>
        </div>

        <EvidenceModal
            :visible="evidenceModalVisible"
            evidence-category="COMPLIANCE"
            :entity-idx="workflowIdx"
            :read-only="isSettled"
            :modal-title-override="workflowName ? `증빙자료 - ${workflowName}` : '증빙자료 - 이행 정산'"
            @close="evidenceModalVisible = false"
            @updated="onEvidenceUpdated"
        />
    </b-modal>
</template>

<script>
import carbonApi from "../../../api";
import { unwrapResponse, extractErrorMessage } from "../../../utils/api.js";
import EvidenceModal from "../../activity/modals/EvidenceModal.vue";

export default {
    name: "ComplianceSettlementModal",
    components: { EvidenceModal },
    props: {
        workflowIdx: { type: Number, default: null },
        workflowName: { type: String, default: "" },
    },
    data() {
        return {
            step: 1,
            loading: false,
            saving: false,
            loadError: null,
            context: null,
            previewDraft: null,
            evidenceModalVisible: false,
            complianceEvidenceFiles: [],
            form: {
                carryInT: 0,
                borrowT: 0,
                offsetT: 0,
                certificateNo: "",
                vendorName: "",
                borrowEvidenceFileIdx: null,
                borrowApprovalRef: "",
                offsetEvidenceFileIdx: null,
            },
        };
    },
    computed: {
        modalTitle() {
            return "이행 정산";
        },
        isSettled() {
            return this.context?.complianceStat === "SETTLED";
        },
        maxCarryInT() {
            if (!this.context?.draft) return 0;
            const avail = this.context.availableCarryInT || 0;
            const deficit =
                Math.max(0, (this.context.grossActualT || 0) - (this.context.assignedCapT || 0));
            return Math.min(avail, deficit);
        },
        maxBorrowT() {
            return this.context?.draft?.maxBorrowKg ? this.context.draft.maxBorrowKg / 1000 : 0;
        },
        maxOffsetT() {
            return this.context?.draft?.maxOffsetKg ? this.context.draft.maxOffsetKg / 1000 : 0;
        },
        borrowEvidenceOptions() {
            const list = (this.complianceEvidenceFiles || [])
                .filter((f) => f && f.evidenceType === "BORROW_APPROVAL")
                .map((f) => ({
                    value: f.fileIdx,
                    text: `${f.title || f.fileName || `파일#${f.fileIdx}`}${f.fileType ? ` (${f.fileType})` : ""}`,
                }));
            return [{ value: null, text: "선택하세요" }, ...list];
        },
        offsetEvidenceOptions() {
            const list = (this.complianceEvidenceFiles || [])
                .filter((f) => f && f.evidenceType === "OFFSET_CERTIFICATE")
                .map((f) => ({
                    value: f.fileIdx,
                    text: `${f.title || f.fileName || `파일#${f.fileIdx}`}${f.fileType ? ` (${f.fileType})` : ""}`,
                }));
            return [{ value: null, text: "선택하세요" }, ...list];
        },
    },
    methods: {
        formatT(v) {
            const n = Number(v);
            if (!Number.isFinite(n)) return "—";
            return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
        },
        show() {
            this.$refs.modal.show();
            this.load();
        },
        hide() {
            this.$refs.modal.hide();
        },
        onHidden() {
            this.step = 1;
            this.context = null;
            this.previewDraft = null;
            this.loadError = null;
            this.evidenceModalVisible = false;
            this.complianceEvidenceFiles = [];
            this.form.borrowEvidenceFileIdx = null;
            this.form.borrowApprovalRef = "";
            this.form.offsetEvidenceFileIdx = null;
        },
        openEvidenceModal() {
            this.evidenceModalVisible = true;
        },
        async onEvidenceUpdated() {
            await this.loadComplianceEvidenceFiles();
        },
        async loadComplianceEvidenceFiles() {
            if (!this.workflowIdx) return;
            try {
                const res = await carbonApi.evidenceFile.getEvidenceFiles({
                    evidenceCategory: "COMPLIANCE",
                    entityIdx: this.workflowIdx,
                });
                const data = res?.data?.data || res?.data;
                this.complianceEvidenceFiles = data?.files || [];
            } catch {
                this.complianceEvidenceFiles = [];
            }
        },
        async load() {
            if (!this.workflowIdx) return;
            this.loading = true;
            this.loadError = null;
            try {
                const res = await carbonApi.workflow.getComplianceContext(this.workflowIdx);
                this.context = unwrapResponse(res);
                this.previewDraft = this.context.draft;
                await this.loadComplianceEvidenceFiles();
                if (this.context.draft) {
                    this.form.carryInT = this.context.draft.carryInAppliedKg / 1000;
                    this.form.borrowT = this.context.draft.borrowAppliedKg / 1000;
                    this.form.offsetT = this.context.draft.offsetAppliedKg / 1000;
                }
                if (this.isSettled) {
                    const settled = await carbonApi.workflow.getComplianceSettlement(this.workflowIdx);
                    const data = unwrapResponse(settled);
                    if (data.snapshot) {
                        this.form.carryInT = data.snapshot.carryInAppliedKg / 1000;
                        this.form.borrowT = data.snapshot.borrowAppliedKg / 1000;
                        this.form.offsetT = data.snapshot.offsetAppliedKg / 1000;
                    }
                    if (Array.isArray(data.ledger) && data.ledger.length > 0) {
                        const borrow = data.ledger.find((l) => l.flexType === "BORROW" && l.evidenceFileIdx);
                        const offset = data.ledger.find((l) => l.flexType === "OFFSET" && l.evidenceFileIdx);
                        if (borrow?.evidenceFileIdx) this.form.borrowEvidenceFileIdx = borrow.evidenceFileIdx;
                        if (offset?.evidenceFileIdx) this.form.offsetEvidenceFileIdx = offset.evidenceFileIdx;
                    }
                }
            } catch (e) {
                this.loadError = extractErrorMessage(e, "이행 정산 정보를 불러오지 못했습니다.");
            } finally {
                this.loading = false;
            }
        },
        goStep2() {
            this.step = 2;
            this.refreshPreview();
        },
        async refreshPreview() {
            if (!this.workflowIdx) return;
            try {
                const body = this.buildPayload();
                const res = await carbonApi.workflow.buildComplianceSettlementDraft(this.workflowIdx, body);
                const data = unwrapResponse(res);
                this.previewDraft = data.draft;
            } catch (e) {
                /* ignore preview errors */
            }
        },
        buildPayload() {
            const payload = {
                carryInAppliedKg: Math.round((Number(this.form.carryInT) || 0) * 1000 * 1000) / 1000,
                borrowAppliedKg: Math.round((Number(this.form.borrowT) || 0) * 1000 * 1000) / 1000,
                offsetAppliedKg: Math.round((Number(this.form.offsetT) || 0) * 1000 * 1000) / 1000,
            };
            if (payload.borrowAppliedKg > 0) {
                payload.borrow = {
                    evidenceFileIdx: this.form.borrowEvidenceFileIdx || null,
                    approvalRef: (this.form.borrowApprovalRef || "").trim() || null,
                };
            }
            if (payload.offsetAppliedKg > 0) {
                payload.offset = {
                    evidenceFileIdx: this.form.offsetEvidenceFileIdx || null,
                    certificateNo: this.form.certificateNo || null,
                    vendorName: this.form.vendorName || null,
                };
            }
            return payload;
        },
        async submit() {
            if (this.isSettled) return;
            this.saving = true;
            try {
                const res = await carbonApi.workflow.applyComplianceSettlement(this.workflowIdx, this.buildPayload());
                unwrapResponse(res);
                this.$bvToast.toast("이행 정산이 완료되었습니다.", { variant: "success", title: "완료" });
                this.$emit("settled");
                this.hide();
            } catch (e) {
                // unwrapResponse()가 success:false를 감지하면 Error를 던지며,
                // 이때 Axios response/status가 없는 경우가 있습니다. 그래서 e.payload까지 함께 봅니다.
                const errorPayload = e?.payload ?? e?.response?.data ?? null;
                const err = errorPayload?.error ?? errorPayload ?? null;
                const evidenceRequiredCode = err?.details?.code || err?.code;

                if (evidenceRequiredCode === "EVIDENCE_REQUIRED") {
                    const ok = await this.$bvModal.msgBoxConfirm(
                        "차입/상쇄 증빙이 누락되었습니다. 강제확정(forceSettle=CONFIRM)으로 진행하시겠습니까?",
                        {
                            title: "증빙 누락",
                            okTitle: "강제확정",
                            cancelTitle: "취소",
                            okVariant: "warning",
                            centered: true,
                        },
                    );
                    if (ok) {
                        const payload = { ...this.buildPayload(), forceSettle: "CONFIRM" };
                        const res2 = await carbonApi.workflow.applyComplianceSettlement(this.workflowIdx, payload);
                        unwrapResponse(res2);
                        this.$bvToast.toast("이행 정산이 완료되었습니다. (강제확정)", { variant: "success", title: "완료" });
                        this.$emit("settled");
                        this.hide();
                        return;
                    }
                }
                this.$bvToast.toast(extractErrorMessage(e, "정산에 실패했습니다."), { variant: "danger", title: "오류" });
            } finally {
                this.saving = false;
            }
        },
        async rollbackSettlement() {
            if (!this.isSettled || !this.workflowIdx) return;
            const ok = await this.$bvModal.msgBoxConfirm(
                [
                    "이행 정산(이월·차입·상쇄)을 롤백합니다.",
                    "산정 확정은 유지되고, 정산 입력을 다시 할 수 있습니다.",
                    "익년에 이미 이월 사용·정산이 있으면 거부됩니다.",
                ].join("\n"),
                {
                    title: "이행 정산 롤백",
                    okTitle: "롤백",
                    cancelTitle: "취소",
                    okVariant: "warning",
                    centered: true,
                },
            );
            if (!ok) return;
            this.saving = true;
            try {
                const res = await carbonApi.workflow.rollbackComplianceSettlement(this.workflowIdx);
                unwrapResponse(res);
                this.$bvToast.toast("이행 정산이 롤백되었습니다.", { variant: "success", title: "완료" });
                this.$emit("rolled-back");
                await this.load();
                this.step = 1;
            } catch (e) {
                const errorPayload = e?.payload ?? e?.response?.data ?? null;
                const err = errorPayload?.error ?? errorPayload ?? null;
                const msg =
                    err?.details?.code === "COMPLIANCE_ROLLBACK_BLOCKED" || err?.code === "COMPLIANCE_ROLLBACK_BLOCKED"
                        ? err.message || extractErrorMessage(e, "익년 정산 때문에 롤백할 수 없습니다.")
                        : extractErrorMessage(e, "이행 정산 롤백에 실패했습니다.");
                this.$bvToast.toast(msg, { variant: "danger", title: "오류" });
            } finally {
                this.saving = false;
            }
        },
    },
    watch: {
        "form.carryInT"() {
            if (this.step === 2) this.refreshPreview();
        },
        "form.borrowT"() {
            if (this.step === 2) this.refreshPreview();
        },
        "form.offsetT"() {
            if (this.step === 2) this.refreshPreview();
        },
    },
};
</script>

<style scoped>
:deep(.compliance-settlement-modal-shell .modal-dialog) {
    max-width: 720px;
}

:deep(.compliance-settlement-modal-shell .modal-content) {
    border: 1px solid #d7e0e8;
    border-radius: 16px;
    overflow: hidden;
    box-shadow:
        0 18px 42px rgba(15, 23, 42, 0.12),
        0 2px 12px rgba(15, 23, 42, 0.06);
}

:deep(.compliance-settlement-modal-header) {
    padding: 14px 18px 12px;
    border-bottom: 1px solid #e3e9ef;
    background: linear-gradient(180deg, #fbfcfd 0%, #f6f9fc 100%);
}

:deep(.compliance-settlement-modal-header .modal-title) {
    font-size: 15px;
    font-weight: 700;
    color: #132238;
    letter-spacing: -0.01em;
}

:deep(.compliance-settlement-modal-header .close) {
    color: #7b8da2;
    opacity: 1;
    text-shadow: none;
}

:deep(.compliance-settlement-modal-body) {
    padding: 14px 16px 16px;
    background: linear-gradient(180deg, #f4f8fb 0%, #f8fafc 100%);
}

.compliance-settlement-content {
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-size: 12px;
    color: #1e293b;
}

/* 로딩 / 에러 */
.compliance-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 48px 24px;
}

.loading-dots {
    display: flex;
    gap: 6px;
}

.loading-dots span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;
    animation: compliance-bounce 1.2s ease-in-out infinite;
}

.loading-dots span:nth-child(2) {
    animation-delay: 0.15s;
}

.loading-dots span:nth-child(3) {
    animation-delay: 0.3s;
}

@keyframes compliance-bounce {
    0%,
    80%,
    100% {
        transform: scale(0.6);
        opacity: 0.4;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

.loading-text {
    font-size: 13px;
    color: #64748b;
}

.compliance-error {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 10px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
    font-size: 13px;
}

.error-icon-wrap {
    font-size: 18px;
    flex-shrink: 0;
}

/* 컨텍스트 박스 (ReportCreateModal 톤) */
.compliance-context-box {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: #dcfce7;
    border: 1px solid #bbf7d0;
    border-radius: 10px;
}

.compliance-context-icon {
    font-size: 16px;
    color: #059669;
    flex-shrink: 0;
}

.compliance-context-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.compliance-context-label {
    font-size: 10px;
    font-weight: 600;
    color: #166534;
    letter-spacing: 0.03em;
    text-transform: uppercase;
}

.compliance-context-value {
    font-size: 13px;
    font-weight: 700;
    color: #14532d;
    word-break: break-word;
}

.compliance-settled-badge {
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 999px;
    background: #166534;
    color: #fff;
}

/* 단계 탭 */
.compliance-step-tabs {
    display: inline-flex;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
}

.compliance-step-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    background: transparent;
    border: none;
    border-right: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s;
}

.compliance-step-tab:last-child {
    border-right: none;
}

.compliance-step-tab:hover:not(:disabled) {
    background: #e2e8f0;
    color: #1e293b;
}

.compliance-step-tab.is-active {
    background: #fff;
    color: #0f766e;
    font-weight: 700;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.compliance-step-tab.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.step-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
}

.compliance-step-tab.is-active .step-num {
    background: #10b981;
    color: #fff;
}

/* 섹션 카드 (FactorFormModal 톤) */
.compliance-step-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.form-section {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.92);
    padding: 12px 14px;
    border: 1px solid #e8eef4;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.form-section-flex {
    background: linear-gradient(180deg, rgba(252, 254, 255, 0.95) 0%, rgba(247, 251, 254, 0.95) 100%);
}

.form-section-evidence {
    background: linear-gradient(180deg, rgba(251, 253, 255, 0.9) 0%, rgba(246, 250, 253, 0.9) 100%);
}

.section-header {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-header-split {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}

.section-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-header h6 {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: #2d4256;
}

.section-pill {
    width: 3px;
    height: 14px;
    border-radius: 999px;
    background: #0f766e;
    flex-shrink: 0;
}

.section-pill-muted {
    background: #8fa3b8;
}

/* 현황 요약 */
.summary-kv-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin: 0;
}

.summary-kv-item {
    padding: 10px 12px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
}

.summary-kv-item dt {
    margin: 0 0 4px;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

.summary-kv-item dd {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
}

.summary-kv-item dd .unit {
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
}

.summary-hint {
    margin: 10px 0 0;
    padding: 8px 10px;
    font-size: 11px;
    color: #64748b;
    background: #f1f5f9;
    border-radius: 8px;
    line-height: 1.5;
}

.summary-hint i {
    margin-right: 6px;
    color: #0d9488;
}

/* 폼 */
.flex-form-grid {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

:deep(.compliance-form-label) {
    font-size: 12px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 4px;
}

:deep(.compliance-input),
:deep(.compliance-input.form-control) {
    font-size: 12px;
    background-color: #f1f5f9;
    border-color: #e2e8f0;
    border-radius: 8px;
}

:deep(.compliance-input:focus),
:deep(.compliance-input.form-control:focus) {
    background-color: #fff;
    border-color: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.15);
}

.field-hint {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: #94a3b8;
}

.evidence-intro {
    margin: 0 0 10px;
    font-size: 11px;
    color: #71879b;
    line-height: 1.45;
}

.evidence-upload-btn {
    font-size: 11px;
    font-weight: 600;
    border-radius: 8px;
    white-space: nowrap;
}

/* 미리보기 */
.preview-result-box {
    padding: 12px 14px;
    border-radius: 10px;
    background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
    border: 1px solid #a7f3d0;
}

.preview-result-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 700;
    color: #065f46;
    margin-bottom: 6px;
}

.preview-result-body {
    font-size: 12px;
    color: #047857;
    line-height: 1.55;
}

.preview-result-body strong {
    color: #064e3b;
    font-weight: 700;
}

.preview-extra {
    color: #059669;
}

/* 푸터 (WorkflowFormModal / FactorFormModal 톤) */
.compliance-modal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 4px;
    padding-top: 14px;
    border-top: 1px solid #e3e9ef;
}

.footer-left,
.footer-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

:deep(.footer-btn) {
    min-width: 88px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 600;
}

:deep(.footer-btn.btn-secondary) {
    color: #334155;
    background: #f8fafc;
    border-color: #d8e1ea;
}

:deep(.footer-btn-primary.btn-success) {
    background: #10b981;
    border-color: #10b981;
}

:deep(.footer-btn-primary.btn-success:hover:not(:disabled)) {
    background: #059669;
    border-color: #059669;
}

:deep(.footer-btn-primary.btn-success:disabled) {
    opacity: 0.65;
}

@media (max-width: 576px) {
    .summary-kv-grid {
        grid-template-columns: 1fr;
    }

    .compliance-modal-footer {
        flex-direction: column;
        align-items: stretch;
    }

    .footer-left,
    .footer-right {
        justify-content: flex-end;
    }
}
</style>
